import React, { useMemo, useState } from "react";
import GridSearchTemplate from "../GridSearchTemplate";
import { Checkbox, Form } from "semantic-ui-react";
import { useTextResources, useMessageResources } from "../../language";
import { useConfirm } from "../../hooks/useConfirm";
import RatesDropDown from "../../components/shared/RatesDropDown";
import TextInput from "../../components/shared/TextInput";
import CheckboxWrapper from "../../components/shared/Checkbox";
import { EditButton } from "../../components/shared/icons";
import { PricingTemplates as PricingTemplatesApi } from "../../api/services/next";
import { useCurrentHotel } from "../../hooks/useCurrentHotel";
import TemplateEditor from "./TemplateEditor";
import {
  onError,
  onSuccess,
  onWarning,
} from "../../components/shared/messages";
import CheckBoxToggle from "../../components/shared/CheckBoxToggle";
import { useLoader } from "../../hooks/useLoader";

const PricingTemplates = () => {
  const [data, setData] = useState([]);
  const [showInActive, setShowInActive] = useState(false);
  const [chosenRow, setChosenRow] = useState({});
  const [selected, setSelected] = useState([]);
  const currentHotel = useCurrentHotel();
  const confirm = useConfirm();
  const text = useTextResources();
  const message = useMessageResources();
  const loader = useLoader();
  const isOpenModal = Object.keys(chosenRow).length > 0;

  const onSearchComplete = (data) => {
    const dataToShow = data.filter((item) => showInActive || item.IsActive);

    setData(dataToShow);
  };

  const editCommissionFormatter = ({ row }) => {
    // edit row click event
    return (
      <EditButton
        content={"EditTemplate"}
        onClick={() => {
          setChosenRow(row);
        }}
      />
    );
  };

  const checkBoxFormatter = ({ value }) => {
    return <Checkbox defaultChecked={value} disabled />;
  };

  const ratesSpec = useMemo(
    () => ({
      HotelId: currentHotel?.Id,
      InActive: showInActive,
    }),
    [currentHotel, showInActive]
  );

  const closeHandler = () => {
    setChosenRow({});
  };

  const setRowName = (val = "", id) => {
    setChosenRow((prev) => ({ ...prev, Id: prev.Id || id, Name: val }));

    if (id) {
      setData((prev) =>
        prev.map((r) => (r.Id != id ? r : { ...r, Name: val }))
      );
    }
  };

  const onGridUpdate = async () => {
    if (selected) {
      const selectedIdsForDeletion = selected.map((index) => data[index].Id);
      const copy = data.map((item) =>
        !selectedIdsForDeletion.includes(item.Id)
          ? item
          : { ...item, IsActive: !item.IsActive }
      );
      confirm({
        header: message("AreYouSure"),
        content: message("AreYouSureYouWouldLikeToDeleteSelectedItems"),
        confirmButton: {
          content: text("Yes"),
          positive: true,
        },
        cancelButton: {
          content: text("No"),
          negative: true,
        },
        onConfirm: async () => {
          try {
            loader(true);
            const res = await PricingTemplatesApi.SavePricingTemplates(copy);
            setSelected([]);
            setData(res.filter((item) => showInActive || item.IsActive));
            onSuccess();
          } catch (error) {
            if (
              error.error?.detail?.includes(
                "Can't change template to in-active"
              )
            )
              onWarning(error);
            else onError(error);
          } finally {
            loader(false);
          }
        },
      });
    }
  };

  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key="Name"
          placeholder={text("Name")}
          label={text("Name")}
          control={TextInput}
          clearable
        />,
        <Form.Field
          label={text("Rate")}
          placeholder={text("All")}
          control={RatesDropDown}
          key="RateIds"
          clearable
          multiple
          spec={ratesSpec}
          width="4"
        />,
        <CheckboxWrapper
          label={text("IncludeInactive")}
          key="IsActive"
          onChange={setShowInActive}
        />,
      ],
      method: "GET",
      api: PricingTemplatesApi.GetPricingTemplates,
      searchOnLoad: true,
      spec: {
        IsActive: showInActive,
      },
    },
    grid: {
      columns: [
        {
          name: "Name",
          title: text("TemplateName"),
        },
        {
          name: "IsActive",
          title: text("Active"),
          formatter: checkBoxFormatter,
          customFilter: CheckBoxToggle,
        },
        {
          name: "Edit",
          title: text("Edit"),
          formatter: editCommissionFormatter,
        },
      ],
      sort: {
        sorting: [{ columnName: "TemplateName", direction: "asc" }],
      },
      editable: {
        allowAdd: true,
        modalEditor: {
          modal: TemplateEditor,
          props: {
            onClose: closeHandler,
            setRowName: setRowName,
            row: chosenRow,
            setTemplates: setData,
            isEditModalOpen: isOpenModal,
          },
        },
      },
      filtering: {
        extensions: [
          { columnName: "Edit", filteringEnabled: false },
          {
            columnName: "Name",
          },
        ],
        defaults: [{ columnName: "IsActive", value: true }],
      },
      menu: [],
      searchOnLoad: true,
      cacheId: "SearchTemplates",
      paging: true,
      selectable: true,
      selectableCallback: (row) => row.IsActive,
      showDeleteMultiple: true,
      columnExtensions: [{ columnName: "Commissions", editingEnabled: false }],
    },
  };

  return (
    <>
      <GridSearchTemplate
        options={gridOptions}
        onSearchComplete={onSearchComplete}
        data={data}
        onGridUpdate={onGridUpdate}
        onSelected={setSelected}
      />
    </>
  );
};
export default PricingTemplates;
